import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAuth, signInWithCustomToken } from 'firebase/auth'

import { useAuthState } from '../../../contexts'
import { useReduxSelector } from '../../../hooks/use-redux-selector'
import { RootState } from '../../../modules'
import { useAssociateEmbedSubmissionMutation, useValidateAuthTokenMutation } from '../../../modules/flexApi'
import { useLogger } from '../../../providers'
import * as Routes from '../../../routing/constants'
import { FullScreenSpinnerLoading } from '../../components'

export const Delegate = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const customerPublicId = urlParams.get('cid')
  const token = urlParams.get('token')
  const client = urlParams.get('client')
  const embedToken = urlParams.get('embedtoken')
  const authProcessedRef = useRef(false)
  const [validateAuthToken] = useValidateAuthTokenMutation()
  const firebaseInitialized = useReduxSelector((state: RootState) => state.auth?.firebaseInitialized)
  const logger = useLogger('Delegate')
  const navigate = useNavigate()
  const { isAnonymous } = useAuthState()
  const [associateEmbedSubmission] = useAssociateEmbedSubmissionMutation()

  useEffect(() => {
    // Prevent re-execution if auth process has already been initiated or user is already authenticated
    if (authProcessedRef.current || !firebaseInitialized || !isAnonymous) {
      return
    }

    // If the customerPublicId or token is not provided, we can not process sign in
    if (!customerPublicId || !token) {
      logger.error('Customer Public ID or Token not provided')
      navigate(Routes.Auth.PHONE)
      return
    }

    const validateToken = async () => {
      try {
        authProcessedRef.current = true
        const response = await validateAuthToken({
          customerId: customerPublicId,
          token,
        }).unwrap()

        if (response.data.token) {
          await signInWithCustomToken(getAuth(), response.data.token)
          if (client && embedToken) {
            await associateEmbedSubmission({ customerId: customerPublicId, client, token: embedToken })
          }
          return
        }

        throw new Error('Invalid token')
      } catch (error: any) {
        logger.error('Error validating token', error?.message)
        navigate(Routes.Auth.PHONE)
      }
    }

    validateToken()
  }, [customerPublicId, token, authProcessedRef, firebaseInitialized, isAnonymous, navigate, validateAuthToken])

  return <FullScreenSpinnerLoading />
}
